import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Charge } from '../models/charge.models';

export interface ExportMeta {
  portalName?: string;
  exportedAt?: Date;
  filtersSummary?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  private platformId = inject(PLATFORM_ID);

  buildChargesCsv(charges: Charge[], formatDate: (d: Date) => string, meta?: ExportMeta): string {
    const BOM = '\uFEFF';
    const lines: string[] = [];
    
    // Cabecera del portal
    const portalName = meta?.portalName || 'Portal de Comercio — Cobros';
    const exportDate = formatDate(meta?.exportedAt || new Date());
    lines.push(`"${portalName}"`);
    lines.push(`"Fecha de exportación: ${exportDate}"`);
    if (meta?.filtersSummary) {
      lines.push(`"${meta.filtersSummary}"`);
    }
    
    // Fila en blanco
    lines.push('');
    
    // Encabezados
    lines.push('ID de cobro,Fecha/Hora,Monto,Tipo de cobro,Estado');
    
    // Datos
    charges.forEach(charge => {
      const row = [
        `"${charge.order_number}"`,
        `"${formatDate(charge.date)}"`,
        charge.amount.toString(),
        `"${charge.type}"`,
        `"${charge.status}"`
      ].join(',');
      lines.push(row);
    });
    
    return BOM + lines.join('\n');
  }

  buildChargesWorkbook(charges: Charge[], formatDate: (d: Date) => string, meta?: ExportMeta): XLSX.WorkBook {
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];
    
    // Cabecera del portal
    const portalName = meta?.portalName || 'Portal de Comercio — Cobros';
    const exportDate = formatDate(meta?.exportedAt || new Date());
    wsData.push([portalName]);
    wsData.push([`Fecha de exportación: ${exportDate}`]);
    if (meta?.filtersSummary) {
      wsData.push([meta.filtersSummary]);
    }
    
    // Fila en blanco
    wsData.push([]);
    
    // Encabezados
    wsData.push(['ID de cobro', 'Fecha/Hora', 'Monto', 'Tipo de cobro', 'Estado']);
    
    // Datos
    charges.forEach(charge => {
      wsData.push([
        charge.order_number,
        formatDate(charge.date),
        charge.amount, // Número real para Excel
        charge.type,
        charge.status
      ]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Formato para columna de monto (columna C, índice 2)
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let row = 5; row <= range.e.r; row++) { // Empezar después de encabezados
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 2 });
      if (ws[cellAddress]) {
        ws[cellAddress].t = 'n'; // Tipo numérico
        ws[cellAddress].z = '#,##0'; // Formato sin decimales
      }
    }
    
    XLSX.utils.book_append_sheet(wb, ws, 'Cobros');
    return wb;
  }

  downloadCsv(content: string, filename: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, filename);
  }

  downloadXlsx(workbook: XLSX.WorkBook, filename: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, filename);
  }

  formatDateForPortal(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  generateFilename(type: 'csv' | 'xlsx'): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    
    return `cobros_${year}${month}${day}_${hours}${minutes}${seconds}.${type}`;
  }
}